import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
  ChevronRight,
  Copy,
  Filter,
  MoreVertical,
  Pencil,
  Plus,
  PowerIcon,
  PowerOff,
  Search,
  Trash2,
  Workflow as WorkflowIcon,
} from "lucide-react";
import { Button } from "@babackapp/ui/components/button";
import { Checkbox } from "@babackapp/ui/components/checkbox";
import { Input } from "@babackapp/ui/components/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@babackapp/ui/components/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@babackapp/ui/components/alert-dialog";
import { cn } from "@babackapp/ui/lib/utils";
import { useWorkflowsEditorConfig } from "../context/workflows-editor-config-context";
import { useWorkflowsEditorWorkflows } from "../context/workflows-editor-workflows-context";
import { useWorkflowSelection } from "../context/workflow-selection-context";
import useCurrentShopSuspense from "../hooks/use-current-shop-suspense";
import { nodeColorToTailwindClasses } from "../lib/node-color-to-tailwind-classes";
import { duplicateWorkflowWithNewIds } from "../lib/duplicate-workflow";
import { Workflow } from "../types";
import { useTriggerLabel, type TriggerLabel } from "./use-trigger-label";
import { CreateWorkflowModal } from "./create-workflow-modal";
import { buildTriggerNode } from "../nodes/trigger/constants";
import type { TriggerNodeDataDto } from "@babackapp/private-api-client/types";

type BulkDialogState = "activate" | "deactivate" | "delete" | null;

export function WorkflowsListSidebar() {
  const { t } = useTranslation("trackoffice");
  const { workflows, currentWorkflow, addWorkflow, onWorkflowSelect } =
    useWorkflowsEditorWorkflows();
  const { selectedIds, toggleSelection, clearSelection, isSelected } =
    useWorkflowSelection();
  const { data: shop } = useCurrentShopSuspense();
  const getTriggerLabel = useTriggerLabel();

  const [search, setSearch] = useState("");
  const [dialogState, setDialogState] = useState<BulkDialogState>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const { api } = useWorkflowsEditorConfig();
  const bulkUpdateMutation = api.useBulkUpdateWorkflows(shop.id);
  const bulkDeleteMutation = api.useBulkDeleteWorkflows(shop.id);
  const addMutation = api.useAddWorkflow(shop.id);
  const isProcessing =
    bulkUpdateMutation.isPending || bulkDeleteMutation.isPending;

  const groups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = query
      ? workflows.filter((w) => w.name.toLowerCase().includes(query))
      : workflows;

    const byTrigger = new Map<
      string,
      { label: string; accent: TriggerLabel["accent"]; Icon: TriggerLabel["Icon"]; items: Workflow[] }
    >();
    for (const w of filtered) {
      const key = w.trigger.data.triggerType;
      const { typeLabel, accent, Icon } = getTriggerLabel(w.trigger);
      if (!byTrigger.has(key))
        byTrigger.set(key, { label: typeLabel, accent, Icon, items: [] });
      byTrigger.get(key)?.items.push(w);
    }

    return [...byTrigger.values()]
      .map((group) => ({
        ...group,
        items: [...group.items].sort((a, b) => {
          if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
          return a.name.localeCompare(b.name);
        }),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [workflows, search, getTriggerLabel]);

  const selectedWorkflows = useMemo(
    () => workflows.filter((w) => selectedIds.has(w.id)),
    [workflows, selectedIds],
  );

  const handleBulkDuplicate = () => {
    selectedWorkflows.forEach((w) => {
      const { definition, trigger } = duplicateWorkflowWithNewIds(w);
      addMutation.mutate({
        requestBody: {
          name: `${w.name} (${t("workflowsEditor.listing.copySuffix", "copy")})`,
          isActive: false,
          isDeletable: true,
          kind: w.kind,
          origin: "USER",
          definition,
          trigger,
        },
      });
    });
    clearSelection();
  };

  const handleCreate = (
    triggerType: TriggerNodeDataDto["triggerType"],
    config: TriggerNodeDataDto["config"],
  ) => {
    addWorkflow({ trigger: buildTriggerNode(triggerType, config) })
      .then((workflow) => onWorkflowSelect?.(workflow.id))
      .catch((error) => console.error("Failed to add workflow:", error));
  };

  const handleBulkActivate = async () => {
    const toActivate = selectedWorkflows.filter((w) => !w.isActive);
    if (toActivate.length > 0) {
      await bulkUpdateMutation.mutateAsync({
        requestBody: {
          workflows: toActivate.map((w) => ({ id: w.id, isActive: true })),
        },
      });
    }
    clearSelection();
    setDialogState(null);
  };

  const handleBulkDeactivate = async () => {
    const toDeactivate = selectedWorkflows.filter((w) => w.isActive);
    if (toDeactivate.length > 0) {
      await bulkUpdateMutation.mutateAsync({
        requestBody: {
          workflows: toDeactivate.map((w) => ({ id: w.id, isActive: false })),
        },
      });
    }
    clearSelection();
    setDialogState(null);
  };

  const handleBulkDelete = async () => {
    const toDelete = selectedWorkflows.filter((w) => w.isDeletable);
    if (toDelete.length > 0) {
      await bulkDeleteMutation.mutateAsync({
        requestBody: { workflowIds: toDelete.map((w) => w.id) },
      });
    }
    clearSelection();
    setDialogState(null);
  };

  const getDialogConfig = () => {
    const count = selectedIds.size;
    switch (dialogState) {
      case "activate":
        return {
          title: t("workflowsEditor.bulkActivateDialog.title", "Activate workflows?"),
          description: t(
            "workflowsEditor.bulkActivateDialog.description",
            "Activate {count} workflow(s)? They will start running automatically.",
            { count },
          ),
          confirm: t("workflowsEditor.bulkActivateDialog.confirm", "Activate"),
          onConfirm: handleBulkActivate,
        };
      case "deactivate":
        return {
          title: t("workflowsEditor.bulkDeactivateDialog.title", "Deactivate workflows?"),
          description: t(
            "workflowsEditor.bulkDeactivateDialog.description",
            "Deactivate {count} workflow(s)? They will stop running automatically.",
            { count },
          ),
          confirm: t("workflowsEditor.bulkDeactivateDialog.confirm", "Deactivate"),
          onConfirm: handleBulkDeactivate,
        };
      case "delete":
        return {
          title: t("workflowsEditor.bulkDeleteDialog.title", "Delete workflows?"),
          description: t(
            "workflowsEditor.bulkDeleteDialog.description",
            "Delete {count} workflow(s)? This action cannot be undone.",
            { count },
          ),
          confirm: t("workflowsEditor.bulkDeleteDialog.confirm", "Delete"),
          onConfirm: handleBulkDelete,
        };
      default:
        return null;
    }
  };

  const dialogConfig = getDialogConfig();
  const hasSelection = selectedIds.size > 0;
  const anyOff = selectedWorkflows.some((w) => !w.isActive);

  return (
    <div className="flex h-full w-80 shrink-0 flex-col border-r bg-background">
      <div className="border-b px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-[15px] font-semibold">
            <WorkflowIcon className="size-[18px] text-muted-foreground" />
            {t("workflowsEditor.listing.title", "Workflows")}
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-bold text-muted-foreground">
              {workflows.length}
            </span>
          </span>
          <Button
            size="icon"
            className="size-7 shrink-0"
            onClick={() => setCreateOpen(true)}
            aria-label={t("workflowsEditor.listing.create", "Create workflow")}
          >
            <Plus className="size-4" />
          </Button>
        </div>

        <div className="relative mt-3">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("workflowsEditor.listing.search", "Search...")}
            className="h-8 pl-7 text-sm"
          />
        </div>

        <div className="mt-3 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          <Filter className="size-3" />
          {t("workflowsEditor.listing.groupedByTrigger", "Grouped by trigger")}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {groups.map((group) => {
          const palette = nodeColorToTailwindClasses(group.accent);
          return (
            <Collapsible key={group.label} defaultOpen className="mb-1">
              <CollapsibleTrigger className="group flex w-full items-center gap-2 rounded-md px-2 py-2 hover:bg-accent">
                <ChevronRight className="size-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90" />
                <span
                  className={cn(
                    "flex size-[22px] shrink-0 items-center justify-center rounded-md",
                    palette.background,
                    palette.text,
                  )}
                >
                  <group.Icon className="size-3" />
                </span>
                <span className="flex-1 truncate text-left text-[12.5px] font-bold">
                  {group.label}
                </span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">
                  {group.items.length}
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent className="flex flex-col gap-0.5 pl-1.5 pt-0.5">
                {group.items.map((workflow) => (
                  <WorkflowRowItem
                    key={workflow.id}
                    workflow={workflow}
                    shopId={shop.id}
                    title={
                      getTriggerLabel(workflow.trigger).summary ?? workflow.name
                    }
                    active={workflow.id === currentWorkflow?.id}
                    checked={isSelected(workflow.id)}
                    onSelect={() => onWorkflowSelect?.(workflow.id)}
                    onToggleCheck={() => toggleSelection(workflow.id)}
                  />
                ))}
              </CollapsibleContent>
            </Collapsible>
          );
        })}
        {groups.length === 0 && (
          <div className="p-5 text-center text-sm text-muted-foreground">
            {t("workflowsEditor.listing.empty", "No workflows")}
          </div>
        )}
      </div>

      {hasSelection && (
        <div className="flex items-center gap-2 border-t bg-background p-2.5">
          <span className="text-[12.5px] font-bold">
            {selectedIds.size}{" "}
            {t("workflowsEditor.listing.selected", "selected")}
          </span>
          <button
            type="button"
            onClick={clearSelection}
            className="text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            {t("workflowsEditor.listing.deleteDialog.cancel", "Cancel")}
          </button>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            disabled={isProcessing}
            onClick={() => setDialogState(anyOff ? "activate" : "deactivate")}
            aria-label={
              anyOff
                ? t("workflowsEditor.listing.actions.activate", "Activate")
                : t("workflowsEditor.listing.actions.deactivate", "Deactivate")
            }
          >
            {anyOff ? (
              <PowerIcon className="size-4" />
            ) : (
              <PowerOff className="size-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8"
            disabled={isProcessing}
            onClick={handleBulkDuplicate}
            aria-label={t("workflowsEditor.listing.actions.duplicate", "Duplicate")}
          >
            <Copy className="size-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="size-8 border-destructive text-destructive hover:bg-destructive/10 hover:text-destructive"
            disabled={isProcessing}
            onClick={() => setDialogState("delete")}
            aria-label={t("workflowsEditor.listing.actions.delete", "Delete")}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      )}

      <CreateWorkflowModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        workflows={workflows}
        onCreate={handleCreate}
      />

      {dialogConfig && (
        <AlertDialog
          open={dialogState !== null}
          onOpenChange={(open) => !open && setDialogState(null)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{dialogConfig.title}</AlertDialogTitle>
              <AlertDialogDescription>
                {dialogConfig.description}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>
                {t("workflowsEditor.listing.deleteDialog.cancel", "Cancel")}
              </AlertDialogCancel>
              <AlertDialogAction onClick={dialogConfig.onConfirm}>
                {dialogConfig.confirm}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}

function WorkflowRowItem({
  workflow,
  shopId,
  title,
  active,
  checked,
  onSelect,
  onToggleCheck,
}: {
  workflow: Workflow;
  shopId: string;
  title: string;
  active: boolean;
  checked: boolean;
  onSelect: () => void;
  onToggleCheck: () => void;
}) {
  const { t } = useTranslation("trackoffice");
  const queryClient = useQueryClient();
  const { api } = useWorkflowsEditorConfig();
  const updateMutation = api.useUpdateWorkflow(shopId);
  const deleteMutation = api.useDeleteWorkflow(shopId);
  const addMutation = api.useAddWorkflow(shopId);

  const handleToggle = () => {
    const queryKey = api.getWorkflowsQueryKey(shopId);
    const previous = queryClient.getQueryData<Workflow[]>(queryKey);
    const next = !workflow.isActive;
    queryClient.setQueryData<Workflow[]>(queryKey, (current) =>
      current?.map((w) => (w.id === workflow.id ? { ...w, isActive: next } : w)),
    );
    updateMutation.mutate(
      { id: workflow.id, requestBody: { isActive: next } },
      { onError: () => queryClient.setQueryData(queryKey, previous) },
    );
  };

  const handleDuplicate = () => {
    const { definition, trigger } = duplicateWorkflowWithNewIds(workflow);
    addMutation.mutate({
      requestBody: {
        name: `${workflow.name} (${t("workflowsEditor.listing.copySuffix", "copy")})`,
        isActive: false,
        isDeletable: true,
        kind: workflow.kind,
        origin: "USER",
        definition,
        trigger,
      },
    });
  };

  return (
    <div
      className={cn(
        "group flex cursor-pointer items-start gap-2.5 rounded-[10px] px-3 py-2.5 transition-colors",
        active
          ? "bg-primary/10 shadow-[inset_2px_0_0_hsl(var(--primary))]"
          : checked
            ? "bg-accent"
            : "hover:bg-accent",
      )}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter") onSelect();
      }}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={onToggleCheck}
        onClick={(e) => e.stopPropagation()}
        className="mt-0.5 shrink-0"
      />
      <span
        title={workflow.isActive ? t("workflowsEditor.header.active", "Active") : t("workflowsEditor.header.inactive", "Inactive")}
        className={cn(
          "mt-1.5 size-[7px] shrink-0 rounded-full",
          workflow.isActive ? "bg-green-500" : "bg-muted-foreground/40",
        )}
      />
      <div className={cn("min-w-0 flex-1", !workflow.isActive && "opacity-65")}>
        <span className="block truncate text-[13.5px] font-bold tracking-tight">
          {title}
        </span>
        <span className="mt-0.5 block truncate text-[11.5px] font-medium text-muted-foreground">
          {workflow.name}
        </span>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(e) => e.stopPropagation()}
            className="flex size-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground opacity-0 transition-opacity hover:bg-background group-hover:opacity-100 data-[state=open]:opacity-100"
            aria-label={t("workflowsEditor.listing.actions.options", "Options")}
          >
            <MoreVertical className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={handleToggle}>
            {workflow.isActive ? (
              <PowerOff className="size-4" />
            ) : (
              <PowerIcon className="size-4" />
            )}
            {workflow.isActive
              ? t("workflowsEditor.listing.actions.deactivate", "Deactivate")
              : t("workflowsEditor.listing.actions.activate", "Activate")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDuplicate}>
            <Copy className="size-4" />
            {t("workflowsEditor.listing.actions.duplicate", "Duplicate")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onSelect}>
            <Pencil className="size-4" />
            {t("workflowsEditor.listing.actions.edit", "Edit")}
          </DropdownMenuItem>
          {workflow.isDeletable && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={() => deleteMutation.mutate(workflow.id)}
              >
                <Trash2 className="size-4" />
                {t("workflowsEditor.listing.actions.delete", "Delete")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
